import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { FileText, Clock, ArrowRight } from 'lucide-react';
import { ConsultationForm } from '../components/ConsultationForm';

export const Resources = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };
  const articles = [
    {
      id: 1,
      category: 'Trademark Basics',
      title: 'What is a Trademark and Why Do You Need One?',
      excerpt: 'Learn the fundamentals of trademark protection and discover why registering your trademark is crucial for brand security.',
      readTime: '5 min read',
      date: 'January 15, 2025'
    },
    {
      id: 2,
      category: 'Registration Process',
      title: 'Step-by-Step Guide to Trademark Registration',
      excerpt: 'A comprehensive walkthrough of the trademark registration process, from initial search to final approval.',
      readTime: '8 min read',
      date: 'January 10, 2025'
    },
    {
      id: 3,
      category: 'Legal Tips',
      title: 'Common Trademark Mistakes to Avoid',
      excerpt: 'Discover the most common pitfalls in trademark registration and how to avoid costly errors.',
      readTime: '6 min read',
      date: 'January 5, 2025'
    },
    {
      id: 4,
      category: 'Copyright',
      title: 'Understanding Copyright Protection for Creative Works',
      excerpt: 'Everything you need to know about protecting your original creative content and artistic works.',
      readTime: '7 min read',
      date: 'December 28, 2024'
    },
    {
      id: 5,
      category: 'Patents',
      title: 'Patent vs. Trademark: Which Do You Need?',
      excerpt: 'Learn the key differences between patents and trademarks to make informed decisions about IP protection.',
      readTime: '5 min read',
      date: 'December 20, 2024'
    },
    {
      id: 6,
      category: 'Business Strategy',
      title: 'Building a Strong IP Portfolio for Your Business',
      excerpt: 'Strategic approaches to developing and managing a comprehensive intellectual property portfolio.',
      readTime: '10 min read',
      date: 'December 15, 2024'
    }
  ];

  const categories = ['All', 'Trademark Basics', 'Registration Process', 'Legal Tips', 'Copyright', 'Patents', 'Business Strategy'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenForm={openForm} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A1F44] to-[#153356]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Resources & Insights
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Expert guidance, legal insights, and helpful resources to navigate the world of intellectual property protection.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-gray-50 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  category === 'All'
                    ? 'bg-[#D4AF37] text-[#0A1F44]'
                    : 'bg-white text-gray-700 hover:bg-[#D4AF37] hover:text-[#0A1F44]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <div key={article.id} className="bg-white rounded-2xl border-2 border-gray-100 hover:border-[#D4AF37] p-6 transition-all duration-300 hover:shadow-xl group">
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-semibold">
                    {article.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{article.readTime}</span>
                    </span>
                    <span>{article.date}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#0A1F44] mb-4">
            Stay Updated with IP Insights
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Subscribe to receive the latest articles, legal updates, and expert tips on intellectual property protection.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 focus:border-[#D4AF37] focus:outline-none"
            />
            <button
              type="submit"
              className="liquid bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold px-8 py-3 rounded-lg whitespace-nowrap"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
      
      <ConsultationForm 
        isOpen={isFormOpen} 
        onClose={closeForm}
        formType="consultation"
      />
    </div>
  );
};